import MetricBox from "components-for/display/metric-box";
import { FunctionComponent, memo, useState } from "react";
import { IProps } from "./interaction-metrics-summary.types";
import { LIVE_POLL_INTERVAL_MILLISECONDS } from "constants/pages/live";
import { useInteractionMetricConversion } from "hooks-for/metrics/use-interaction-metric-conversion";
import { useInteractionMetricsSummary } from "hooks-for/interaction-metrics/use-interaction-metrics-summaries";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import { useInterval } from "react-use";
import "./interaction-metrics-summary.scss";
import { IInteractionsGetQueryParameters } from "@edgetier/watchtower-types";

/**
 * Display a single interactions metric summary value.
 * @param props.getValue          Getter to retrieve the specific interaction metric being displayed.
 * @param props.interactionMetric Interaction metric being displayed.
 * @param props.queryParameters   Any parameters to send to the backend.
 * @param props.metricBoxProps    Other props to pass along.
 * @param props.getDynamicParameters Function for getting any dynamic parameters which should be passed to metrics summary.
 * @returns                       Metric title, value, icon etc.
 */
const InteractionMetricsSummary: FunctionComponent<IProps> = ({
    getValue,
    interactionMetric,
    queryParameters = {},
    getDynamicParameters,
    pollIntervalMilliseconds = LIVE_POLL_INTERVAL_MILLISECONDS,
    ...metricBoxProps
}) => {
    const isAutomaticPoll = typeof getDynamicParameters !== "function";
    const [pollParameters, setPollParameters] = useState<IInteractionsGetQueryParameters>(() =>
        isAutomaticPoll ? {} : getDynamicParameters()
    );

    useInterval(
        () => {
            setPollParameters(isAutomaticPoll ? {} : getDynamicParameters());
        },
        isAutomaticPoll ? null : pollIntervalMilliseconds
    );

    const interactionsQueryParameters = useInteractionQueryParameters({ defaults: queryParameters });
    const options = {
        keepPreviousData: true,
        refetchInterval: isAutomaticPoll ? pollIntervalMilliseconds : undefined,
    };

    const { data, isLoading } = useInteractionMetricsSummary(
        { ...interactionsQueryParameters, ...pollParameters },
        options
    );

    const value = data ? getValue(data) : null;
    const { formatTick, unitShorthand } = useInteractionMetricConversion(interactionMetric, {
        isAggregate: false,
        items: [value ?? 0],
        getValue: (value) => value,
    });

    return (
        <MetricBox
            {...metricBoxProps}
            isLoading={isLoading}
            renderValue={(value) => formatTick(value) + unitShorthand}
            value={value}
        />
    );
};

export default memo(InteractionMetricsSummary);
